import React, { createContext, useContext, useState, useCallback } from 'react';
import analytics from '../services/analyticsService';
import { parseAppPath, isAppModule, DEFAULT_MODULE, MODULOS_DE_CELULAR } from '../lib/appRoute';

/**
 * Carona opcional de navegação: um módulo pode abrir outro já focado em algo
 * (ex.: Equipe & Alocação abre o RACI filtrado em quem não tem papel).
 * Consumidor lê UMA vez e limpa com consumeModulePayload().
 */
export interface ModulePayload {
  raciFocus?: 'sem-papel' | string;
}

export interface UIStateContextValue {
  // Module navigation
  currentModule: string;
  changeModule: (module: string, payload?: ModulePayload) => void;
  consumeModulePayload: () => ModulePayload | null;

  // View controls
  viewMode: string;
  changeViewMode: (mode: string) => void;
  zoom: number;
  changeZoom: (zoom: number) => void;

  // Filters (shared between TaskTable and GanttChart)
  filterByAssigned: string[];
  setFilterByAssigned: (members: string[]) => void;
  toggleFilterMember: (memberId: string) => void;
  clearFilters: () => void;
}

const UIStateContext = createContext<UIStateContextValue | null>(null);

export const useUIState = (): UIStateContextValue => {
  const context = useContext(UIStateContext);
  if (!context) {
    throw new Error('useUIState must be used within UIStateProvider');
  }
  return context;
};

export const UIStateProvider = ({ children }: { children: React.ReactNode }) => {
  // A URL manda, o localStorage e so a memoria de quem voltou pela raiz.
  //
  // A ordem inverteu quando o app ganhou endereco por modulo: com o localStorage
  // na frente, abrir um link profundo (notificacao, atalho do PWA) caia no
  // ultimo modulo aberto na sessao anterior em vez de na tela pedida.
  const [currentModule, setCurrentModule] = useState<string>(() => {
    const fromUrl = parseAppPath(window.location.pathname).module;
    if (fromUrl) return fromUrl;

    const saved = localStorage.getItem('current-module');
    if (!isAppModule(saved)) return DEFAULT_MODULE;

    // No celular, modulo salvo que nao funciona ali nao e destino: e lixo de
    // sessao de desktop.
    //
    // Sem esta guarda, quem ja usou o produto no computador e depois abre
    // meugantt.com.br no telefone cai direto em "Gantt funciona melhor no
    // computador". A primeira tela do app passa a ser o app dizendo que nao
    // serve, e justamente para quem ja e usuario.
    //
    // O `start_url` do manifest nao cobre isso: ele so vale para quem abre pelo
    // icone instalado. Quem digita o dominio, clica num link ou reabre uma aba
    // antiga chega pela raiz e herda o modulo salvo.
    //
    // O estado honesto continua existindo para link DELIBERADO
    // (/p/<id>/gantt colado no WhatsApp), que e onde ele ajuda.
    const ehCelular = typeof window !== 'undefined'
      && window.matchMedia?.('(max-width: 767px)').matches;
    if (ehCelular && !MODULOS_DE_CELULAR.has(saved)) return 'hoje';

    return saved;
  });

  const [viewMode, setViewMode] = useState<string>(() => {
    const saved = localStorage.getItem('gantt-view-mode');
    return saved || 'days';
  });

  const [zoom, setZoom] = useState<number>(() => {
    const saved = localStorage.getItem('gantt-zoom');
    return saved ? parseFloat(saved) : 1;
  });

  const [filterByAssigned, setFilterByAssigned] = useState<string[]>([]);

  // Payload em ref, não em state: é carona de UMA navegação, não estado de
  // render. localStorage fica de fora de propósito (não sobrevive a reload).
  const modulePayloadRef = React.useRef<ModulePayload | null>(null);

  const changeModule = useCallback((moduleName: string, payload?: ModulePayload): void => {
    modulePayloadRef.current = payload ?? null;
    setCurrentModule(moduleName);
    localStorage.setItem('current-module', moduleName);
    analytics.trackModuleAccessed(moduleName);
  }, []);

  const consumeModulePayload = useCallback((): ModulePayload | null => {
    const p = modulePayloadRef.current;
    modulePayloadRef.current = null;
    return p;
  }, []);

  const changeViewMode = useCallback((mode: string): void => {
    setViewMode(mode);
    localStorage.setItem('gantt-view-mode', mode);
  }, []);

  const changeZoom = useCallback((newZoom: number): void => {
    const clampedZoom = Math.max(0.5, Math.min(2, newZoom));
    setZoom(clampedZoom);
    localStorage.setItem('gantt-zoom', clampedZoom.toString());
  }, []);

  const toggleFilterMember = useCallback((memberId: string): void => {
    setFilterByAssigned(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  }, []);

  const clearFilters = useCallback((): void => {
    setFilterByAssigned([]);
  }, []);

  const value: UIStateContextValue = {
    currentModule,
    changeModule,
    consumeModulePayload,
    viewMode,
    changeViewMode,
    zoom,
    changeZoom,
    filterByAssigned,
    setFilterByAssigned,
    toggleFilterMember,
    clearFilters,
  };

  return (
    <UIStateContext.Provider value={value}>
      {children}
    </UIStateContext.Provider>
  );
};
